import { crmWorkspace } from './crmWorkspace';
// LOOP CRM PROVIDER-AGNOSTIC IDENTITY R35 — Firebase UID is canonical; email and verified phone are fallbacks, independent of auth provider.
// LOOP CRM FLAWLESS DEDUPE PREVENTION R29 — compatible exact-name resolution, fail-closed imports, and no deterministic duplicate ids.
// src/utils/customerCrm.js
// LOOP CRM SEARCH IMPORT REDESIGN V24 — import de-dupes by contact/name, stores searchable fields, and updates existing CRM rows.
// LOOP CUSTOMER DOCUMENT ID INVARIANT V20 — canonical Firestore document ids survive stale/null id fields and prevent null path crashes.
// LOOP CUSTOMER IDENTITY BULLETPROOF V19 — contact identity wins over temporary/manual IDs; only true cross-contact conflicts block bookings.
// LOOP IDENTITY PRIORITY V4.4 — Cloud Function resolves ProfilePage/users identity canonically
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
  increment,
  writeBatch,
  setDoc,
  limit,
  orderBy,
  startAfter,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { getFunctions, httpsCallable } from "firebase/functions";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "./nativeHttp";

function onlyDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

export function normalizePhone(value) {
  const digits = onlyDigits(value);
  if (!digits) return "";
  if (digits.length === 8) return `+507${digits}`;
  if (digits.length === 11 && digits.startsWith("507")) return `+${digits}`;
  if (String(value || "").trim().startsWith("+")) return `+${digits}`;
  return digits ? `+${digits}` : "";
}


export function whatsappPhoneStatus(value) {
  const phone = normalizePhone(value);
  if (!phone) return "missing";
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 10 ? "valid" : "invalid";
}

export function normalizeWhatsappPreference(source = {}) {
  const optOut = source.whatsappOptOut === true || source.customerWhatsappOptOut === true || source.noWhatsapp === true || source.doNotWhatsapp === true || source.whatsappOptIn === false || source.customerWhatsappConsent === false;
  return {
    whatsappOptOut: optOut,
    whatsappOptIn: !optOut,
    customerWhatsappConsent: !optOut,
    whatsappPhoneStatus: source.whatsappPhoneStatus || whatsappPhoneStatus(source.phoneNormalized || source.phone || source.customerPhone || source.whatsapp || ""),
  };
}

export function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function customerRecordDocumentId(customer = {}, explicitDocumentId = "") {
  return String(
    explicitDocumentId ||
    customer?.__documentId ||
    customer?.docId ||
    customer?.id ||
    customer?.customerId ||
    customer?.clientId ||
    ""
  ).trim();
}

function canonicalCustomerRecord(customer = {}, explicitDocumentId = "") {
  const source = customer && typeof customer === "object" ? customer : {};
  const id = customerRecordDocumentId(source, explicitDocumentId);
  return {
    ...source,
    __documentId: id || source.__documentId || null,
    id: id || null,
    customerId: id || source.customerId || null,
    clientId: id || source.clientId || null,
  };
}

function customer360Number(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function customer360Date(value) {
  try {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (value.seconds) return new Date(Number(value.seconds) * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

function customer360DaysSince(dateLike) {
  const d = customer360Date(dateLike);
  if (!d) return null;
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (!Number.isFinite(diff)) return null;
  return Math.max(0, Math.floor(diff / 86400000));
}

export function buildCustomer360Snapshot(customer = {}, extra = {}) {
  const totalBookings = customer360Number(
    customer.totalBookings ??
    customer.importedTotalBookings ??
    customer.customerSnapshot?.totalBookings ??
    extra.totalBookings,
    0
  );
  const completedBookings = customer360Number(
    customer.completedBookings ??
    customer.completed ??
    customer.customerSnapshot?.completedBookings ??
    extra.completedBookings,
    0
  );
  const cancelledBookings = customer360Number(
    customer.cancelledBookings ??
    customer.cancelled ??
    customer.customerSnapshot?.cancelledBookings ??
    extra.cancelledBookings,
    0
  );
  const noShowBookings = customer360Number(
    customer.noShowBookings ??
    customer.noShow ??
    customer.customerSnapshot?.noShowBookings ??
    extra.noShowBookings,
    0
  );
  const totalSpent = customer360Number(
    customer.totalSpent ??
    customer.lifetimeValue ??
    customer.customerSnapshot?.totalSpent ??
    extra.totalSpent,
    0
  );
  const lastBookingAt =
    customer.lastBookingAt ||
    customer.lastVisitAt ||
    customer.customerSnapshot?.lastBookingAt ||
    extra.lastBookingAt ||
    null;
  const daysSinceLastVisit = customer360DaysSince(lastBookingAt);
  const averageTicket = totalBookings > 0 ? totalSpent / totalBookings : 0;

  let segment = "Nuevo";
  let tone = "new";
  if (totalBookings >= 8 || totalSpent >= 500) {
    segment = "VIP";
    tone = "vip";
  } else if (totalBookings >= 3) {
    segment = "Recurrente";
    tone = "loyal";
  }

  let risk = "Sin historial";
  let riskTone = "neutral";
  if (totalBookings > 0) {
    if (noShowBookings >= 2 || cancelledBookings >= 3) {
      risk = "Riesgo operativo";
      riskTone = "warning";
    } else if (daysSinceLastVisit != null && daysSinceLastVisit >= 90) {
      risk = "En riesgo";
      riskTone = "risk";
    } else if (daysSinceLastVisit != null && daysSinceLastVisit <= 30) {
      risk = "Activo";
      riskTone = "active";
    } else {
      risk = "Seguimiento";
      riskTone = "neutral";
    }
  }

  const nextAction =
    totalBookings <= 0
      ? "Primera visita: confirma datos y preferencias."
      : riskTone === "risk"
        ? "Cliente sin visita reciente: considera mensaje de reactivación."
        : riskTone === "warning"
          ? "Revisar cancelaciones/no-shows antes de confirmar."
          : tone === "vip"
            ? "Cliente VIP: cuidar experiencia y preferencias."
            : "Cliente recurrente: revisar servicio favorito y notas.";

  return {
    segment,
    tone,
    risk,
    riskTone,
    nextAction,
    totalBookings,
    completedBookings,
    cancelledBookings,
    noShowBookings,
    totalSpent,
    averageTicket,
    daysSinceLastVisit,
    lastBookingAt,
    lastBookingAtText: customer.importedLastBookingAtText || extra.importedLastBookingAtText || "",
    favoriteService: customer.favoriteService || customer.customerSnapshot?.favoriteService || extra.favoriteService || "",
    notes: customer.notes || customer.customerSnapshot?.notes || "",
  };
}

export function buildCustomerSearchKeys({ name = "", phone = "", email = "" } = {}) {
  const phoneNormalized = normalizePhone(phone);
  const emailNormalized = normalizeEmail(email);
  const nameKey = String(name || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return { phoneNormalized, emailNormalized, nameKey };
}

export function customerPayloadFromBooking(input = {}) {
  const name = input.customerName || input.clientName || input.name || input.customer || "Cliente";
  const phone = input.customerPhone || input.clientPhone || input.phoneNormalized || input.phone || input.telefonoNormalized || input.telefono || input.mobile || input.whatsapp || "";
  const email = input.customerEmail || input.clientEmail || input.emailNormalized || input.email || input.contactEmail || "";

  const { phoneNormalized, emailNormalized, nameKey } = buildCustomerSearchKeys({ name, phone, email });

  const explicitCustomerId = String(
    input.customerId ||
    input.clientId ||
    input.id ||
    input.customer?.id ||
    input.customer?.customerId ||
    input.customerSnapshot?.id ||
    input.customerSnapshot?.customerId ||
    ""
  ).trim();
  const linkedUid = input.linkedUserUid || input.linkedUserId || input.userUid || input.userId || input.customerUserUid || input.customerUserId || input.customerSnapshot?.linkedUserUid || input.customerSnapshot?.linkedUserId || input.customerSnapshot?.userUid || input.customerSnapshot?.userId || null;

  return {
    id: explicitCustomerId || null,
    customerId: explicitCustomerId || null,
    clientId: explicitCustomerId || null,
    name: String(name || "").trim() || "Cliente",
    phone: phoneNormalized || String(phone || "").trim(),
    phoneNormalized,
    email: emailNormalized,
    emailNormalized,
    nameKey,
    notes: input.notes || input.customerNotes || "",
    tags: Array.isArray(input.tags) ? input.tags : [],
    ...normalizeWhatsappPreference({ ...input, phone: phoneNormalized || phone }),
    source: input.source || "manual",
    hasUserAccount: Boolean(linkedUid),
    linkedUserId: linkedUid,
    linkedUserUid: linkedUid,
    userId: linkedUid,
    userUid: linkedUid,
    importedTotalBookings: Number(input.importedTotalBookings || 0),
    importedLastBookingAtText: input.importedLastBookingAtText || "",
    importedRewardPointsText: input.importedRewardPointsText || "",
    searchableName: nameKey,
    searchText: [nameKey, phoneNormalized, onlyDigits(phoneNormalized || phone), emailNormalized, input.notes || input.customerNotes || "", Array.isArray(input.tags) ? input.tags.join(" ") : ""].filter(Boolean).join(" ").trim(),
    phoneDigits: onlyDigits(phoneNormalized || phone),
    whatsappDigits: onlyDigits(phoneNormalized || phone),
  };
}


function firstNonEmpty(...values) {
  for (const value of values) {
    const s = String(value || "").trim();
    if (s) return s;
  }
  return "";
}

function platformUserName(data = {}, fallback = "Cliente") {
  const first = firstNonEmpty(data.firstName, data.nombre);
  const last = firstNonEmpty(data.lastName, data.apellido);
  const firstLast = `${first} ${last}`.replace(/\s+/g, " ").trim();
  return firstNonEmpty(
    firstLast,
    data.fullName,
    data.name,
    data.displayName,
    data.email,
    fallback,
    "Cliente"
  );
}

function platformUserIdentityFromDoc(docSnap, matchedBy = "platform", sourceCollection = "users") {
  if (!docSnap || !docSnap.exists?.()) return null;
  const data = docSnap.data() || {};
  const uid = String(data.uid || data.userUid || data.userId || docSnap.id || "").trim();
  if (!uid) return null;

  const email = normalizeEmail(firstNonEmpty(
    data.emailNormalized,
    data.contactEmailNormalized,
    data.authEmailNormalized,
    data.email,
    data.authEmail,
    data.contactEmail,
    data.userEmail
  ));

  const legacyContactPhone = normalizePhone(firstNonEmpty(
    data.phoneE164,
    data.phoneNormalized,
    data.telefonoNormalized,
    data.telefono,
    data.phone,
    data.phoneDisplay,
    data.whatsapp
  ));
  const verifiedPhone = data.phoneVerified === true
    ? normalizePhone(data.phoneVerifiedE164 || "")
    : "";

  const isRealLoopUser = sourceCollection === "users";
  return {
    uid,
    id: uid,
    userUid: uid,
    userId: uid,
    linkedUserUid: uid,
    linkedUserId: uid,
    name: platformUserName(data, email || verifiedPhone || "Cliente"),
    email,
    emailNormalized: email,

    // R34: a Loop account's phone identity is ONLY its verified phone.
    // Legacy/unverified profile numbers remain contact metadata and cannot link UIDs.
    phone: verifiedPhone,
    phoneNormalized: verifiedPhone,
    phoneE164: verifiedPhone,
    phoneDisplay: verifiedPhone ? (data.phoneDisplay || verifiedPhone) : "",
    phoneVerified: Boolean(verifiedPhone),
    phoneVerifiedE164: verifiedPhone || "",
    profileContactPhone: legacyContactPhone || "",
    hasProfilePhone: isRealLoopUser ? Boolean(verifiedPhone) : false,

    isRealLoopUser,
    matchedBy,
    sourceCollection,
    source: sourceCollection === "pendingRegistrations" ? "pending_registration_match" : "platform_user_match",
    raw: data,
  };
}

async function customerR29ReadCustomerById(customersCol, customerId) {
  const id = String(customerId || "").trim();
  const path = String(customersCol?.path || "").trim();
  if (!id || !path) return null;

  if (isIosCapacitor()) {
    const row = await fsClient.getDocByPath(`${path}/${id}`);
    return row ? canonicalCustomerRecord(row, row.__documentId || row.docId || row.id || id) : null;
  }

  const snap = await getDoc(doc(customersCol, id));
  return snap.exists() ? canonicalCustomerRecord(snap.data() || {}, snap.id) : null;
}

async function customerR29ResolveActiveRecord(customersCol, rawCustomer) {
  let current = rawCustomer ? canonicalCustomerRecord(rawCustomer, rawCustomer.__documentId || rawCustomer.docId || rawCustomer.id) : null;
  const visited = new Set();

  for (let depth = 0; current && depth < 6; depth += 1) {
    const currentId = String(current.id || current.customerId || current.clientId || "").trim();
    if (currentId) {
      if (visited.has(currentId)) return null;
      visited.add(currentId);
    }

    if (customerR29IsActive(current)) return current;

    const targetId = String(current.mergedIntoCustomerId || current.mergedIntoCustomer || "").trim();
    if (!targetId) return null;
    // eslint-disable-next-line no-await-in-loop
    current = await customerR29ReadCustomerById(customersCol, targetId);
  }

  return null;
}

async function findCustomerFieldResultR29(customersCol, field, value, maxRows = 25) {
  if (!value) return { customer: null, conflict: null, candidates: [] };

  let rawRows = [];
  if (isIosCapacitor()) {
    const path = String(customersCol?.path || "").trim();
    if (!path) return { customer: null, conflict: null, candidates: [] };
    rawRows = await fsClient.listCollectionByPath(path, {
      where: [{ field, op: "EQUAL", value }],
      limit: maxRows,
    });
  } else {
    const snap = await getDocs(query(customersCol, where(field, "==", value), limit(maxRows)));
    rawRows = snap.docs.map((d) => canonicalCustomerRecord(d.data() || {}, d.id));
  }

  if (Array.isArray(rawRows) && rawRows.length >= maxRows) {
    return {
      customer: null,
      candidates: [],
      conflict: {
        type: "customer_field_too_many_matches",
        field,
        message: "Encontramos demasiados perfiles con este mismo dato. Ejecuta la limpieza o selecciona el cliente correcto antes de continuar.",
      },
    };
  }

  const resolved = [];
  const seen = new Set();
  for (const rawRow of Array.isArray(rawRows) ? rawRows : []) {
    // eslint-disable-next-line no-await-in-loop
    const active = await customerR29ResolveActiveRecord(customersCol, rawRow);
    const id = String(active?.id || "").trim();
    if (!active || !id || seen.has(id)) continue;
    seen.add(id);
    resolved.push(active);
  }

  if (!resolved.length) return { customer: null, conflict: null, candidates: [] };

  const state = customerR29IdentityConflict(resolved);
  const isUidField = /(?:uid|userid)$/i.test(String(field || ""));
  const isPhoneField = /phone|telefono|whatsapp/i.test(String(field || ""));
  const isEmailField = /email/i.test(String(field || ""));
  const conflicts = isUidField
    ? false
    : isPhoneField
      ? state.uids.length > 1 || state.emails.length > 1
      : isEmailField
        ? state.uids.length > 1 || state.phones.length > 1
        : state.conflict;

  if (conflicts) {
    return {
      customer: null,
      candidates: resolved,
      conflict: {
        type: "customer_field_identity_conflict",
        field,
        message: "Encontramos varios perfiles con este dato y otras identidades diferentes. Selecciona el cliente correcto antes de continuar.",
        customerIds: resolved.map((row) => row.id),
        identitySummary: {
          uidCount: state.uids.length,
          phoneCount: state.phones.length,
          emailCount: state.emails.length,
        },
      },
    };
  }

  return {
    customer: [...resolved].sort(customerR29StableCompare)[0] || null,
    conflict: null,
    candidates: resolved,
  };
}

async function findFirstCustomerByField(customersCol, field, value) {
  const result = await findCustomerFieldResultR29(customersCol, field, value, 25);
  if (result.conflict) {
    const err = new Error(result.conflict.message || "customer_field_identity_conflict");
    err.code = "customer_field_identity_conflict";
    err.conflict = result.conflict;
    throw err;
  }
  return result.customer || null;
}

async function findCustomerByExplicitId(db, businessId, customerId) {
  const id = String(customerId || "").trim();
  if (!db || !businessId || !id) return null;
  try {
    const customersCol = collection(db, `businesses/${businessId}/customers`);
    const direct = await customerR29ReadCustomerById(customersCol, id);
    const active = await customerR29ResolveActiveRecord(customersCol, direct);
    return active ? { ...active, identityMatchedBy: active.id === id ? "explicit_customer_id" : "explicit_merged_customer_id" } : null;
  } catch (error) {
    console.warn("[customerCrm] explicit customer lookup failed", error);
  }
  return null;
}

// LOOP CRM IDENTITY RESOLVER R29
// A name-only booking must reuse a compatible existing profile instead of
// creating one more duplicate every time the same name is typed.
function customerR29IsActive(customer = {}) {
  return !(
    customer.hiddenFromCrm === true ||
    customer.archived === true ||
    customer.mergedIntoCustomerId ||
    customer.mergedIntoCustomer
  );
}

function customerR29Uid(customer = {}) {
  return firstNonEmpty(
    customer.linkedUserUid,
    customer.linkedUserId,
    customer.userUid,
    customer.userId,
    customer.platformUserUid,
    customer.platformUserId
  );
}

function customerR29Phone(customer = {}) {
  return normalizePhone(
    customer.phoneNormalized ||
    customer.phoneE164 ||
    customer.phone ||
    customer.customerPhone ||
    customer.whatsapp ||
    customer.whatsApp ||
    ""
  );
}

function customerR29Email(customer = {}) {
  return normalizeEmail(
    customer.emailNormalized ||
    customer.email ||
    customer.customerEmail ||
    customer.clientEmail ||
    ""
  );
}

function customerR29DateMs(value) {
  try {
    if (!value) return Number.MAX_SAFE_INTEGER;
    if (typeof value.toDate === "function") return value.toDate().getTime();
    if (value.seconds) return Number(value.seconds) * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  } catch (_) {
    return Number.MAX_SAFE_INTEGER;
  }
}

function customerR29Score(customer = {}) {
  const totalBookings = Math.max(
    Number(customer.totalBookings || 0) || 0,
    Number(customer.importedTotalBookings || 0) || 0,
    Number(customer.bookingsCount || 0) || 0
  );
  const completeness = [
    customer.name || customer.customerName || customer.displayName,
    customerR29Phone(customer),
    customerR29Email(customer),
    customer.notes || customer.customerNotes,
    Array.isArray(customer.tags) && customer.tags.length,
  ].filter(Boolean).length;

  return (
    (customerR29Uid(customer) ? 1000000 : 0) +
    (customerR29Phone(customer) ? 100000 : 0) +
    (customerR29Email(customer) ? 50000 : 0) +
    (customer.hasUserAccount === true ? 25000 : 0) +
    (customer.hasLoyalty === true || customer.loyaltyQrEnabled === true ? 10000 : 0) +
    (customer.membershipActive === true || customer.hasActiveMembership === true ? 8000 : 0) +
    totalBookings * 100 +
    completeness * 10
  );
}

function customerR29StableCompare(a = {}, b = {}) {
  const scoreDiff = customerR29Score(b) - customerR29Score(a);
  if (scoreDiff) return scoreDiff;
  const createdDiff = customerR29DateMs(a.createdAt) - customerR29DateMs(b.createdAt);
  if (createdDiff) return createdDiff;
  return String(a.id || "").localeCompare(String(b.id || ""));
}

function customerR29IdentityConflict(rows = []) {
  const active = (Array.isArray(rows) ? rows : []).filter(customerR29IsActive);
  const uids = new Set(active.map(customerR29Uid).filter(Boolean));
  const phones = new Set(active.map(customerR29Phone).filter(Boolean));
  const emails = new Set(active.map(customerR29Email).filter(Boolean));
  const strongRows = active.filter((row) => (
    customerR29Uid(row) || customerR29Phone(row) || customerR29Email(row)
  ));

  let connectedBy = strongRows.length <= 1 ? "single_or_empty" : "";
  if (!connectedBy && uids.size === 1 && strongRows.every((row) => customerR29Uid(row))) connectedBy = "uid";
  if (!connectedBy && phones.size === 1 && uids.size <= 1 && emails.size <= 1 && strongRows.every((row) => customerR29Phone(row))) connectedBy = "phone";
  if (!connectedBy && emails.size === 1 && uids.size <= 1 && phones.size <= 1 && strongRows.every((row) => customerR29Email(row))) connectedBy = "email";
  const connected = Boolean(connectedBy);

  return {
    conflict: !connected,
    connected,
    connectedBy,
    strongRowCount: strongRows.length,
    uids: Array.from(uids),
    phones: Array.from(phones),
    emails: Array.from(emails),
  };
}

async function listCustomersByNameKeyR29(customersCol, nameKey, maxRows = 50) {
  const key = String(nameKey || "").trim();
  if (!key || key.length < 3) return [];

  if (isIosCapacitor()) {
    const path = String(customersCol?.path || "").trim();
    if (!path) return [];
    const rows = await fsClient.listCollectionByPath(path, {
      where: [{ field: "nameKey", op: "EQUAL", value: key }],
      limit: maxRows,
    });
    return (Array.isArray(rows) ? rows : [])
      .map((row) => canonicalCustomerRecord(row, row.__documentId || row.docId || row.id))
      .filter(customerR29IsActive);
  }

  const snap = await getDocs(query(customersCol, where("nameKey", "==", key), limit(maxRows)));
  return snap.docs
    .map((d) => canonicalCustomerRecord(d.data() || {}, d.id))
    .filter(customerR29IsActive);
}

async function findCompatibleCustomerByNameKeyR29(customersCol, nameKey) {
  const rows = await listCustomersByNameKeyR29(customersCol, nameKey, 101);
  if (rows.length >= 101) {
    return {
      customer: null,
      conflict: {
        type: "customer_name_too_many_matches",
        message: "Hay demasiados perfiles con este mismo nombre para resolverlos automáticamente. Selecciona el cliente correcto.",
        customerIds: rows.slice(0, 100).map((row) => row.id).filter(Boolean),
      },
      duplicateCandidateIds: [],
    };
  }
  if (!rows.length) return { customer: null, conflict: null, duplicateCandidateIds: [] };

  const identityState = customerR29IdentityConflict(rows);
  if (identityState.conflict) {
    return {
      customer: null,
      conflict: {
        type: "customer_name_identity_conflict",
        message: "Hay varios clientes con este mismo nombre y datos diferentes. Selecciona el perfil correcto antes de continuar.",
        customerIds: rows.map((row) => row.id).filter(Boolean),
        identitySummary: {
          uidCount: identityState.uids.length,
          phoneCount: identityState.phones.length,
          emailCount: identityState.emails.length,
        },
      },
      duplicateCandidateIds: [],
    };
  }

  const sorted = [...rows].sort(customerR29StableCompare);
  return {
    customer: {
      ...sorted[0],
      identityMatchedBy: rows.length === 1 ? "nameKey_unique" : "nameKey_compatible_r29",
      identityCompatibleDuplicateIds: sorted.slice(1).map((row) => row.id).filter(Boolean),
    },
    conflict: null,
    duplicateCandidateIds: sorted.slice(1).map((row) => row.id).filter(Boolean),
  };
}

async function findUniqueCustomerByNameKey(customersCol, nameKey) {
  const result = await findCompatibleCustomerByNameKeyR29(customersCol, nameKey);
  if (result?.conflict) {
    const err = new Error(result.conflict.message || "customer_name_identity_conflict");
    err.code = "customer_name_identity_conflict";
    err.conflict = result.conflict;
    throw err;
  }
  return result?.customer || null;
}

async function findCustomerByUid(customersCol, uid) {
  const cleanUid = String(uid || "").trim();
  if (!cleanUid) return null;
  const fields = ["linkedUserUid", "linkedUserId", "userUid", "userId", "platformUserUid", "platformUserId"];
  for (const field of fields) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const found = await findFirstCustomerByField(customersCol, field, cleanUid);
      if (found) return { ...found, identityMatchedBy: field };
    } catch (error) {
      console.warn("[customerCrm] uid customer lookup failed", field, error);
    }
  }
  return null;
}

async function findCustomerIdentityMatches(db, businessId, { id, customerId, clientId, phoneNormalized, emailNormalized, nameKey, linkedUserUid, linkedUserId, userUid, userId } = {}) {
  const customersCol = collection(db, `businesses/${businessId}/customers`);
  const explicitId = customerId || clientId || id || "";
  const uid = linkedUserUid || linkedUserId || userUid || userId || "";

  const [idCustomer, uidResult, phoneResult, emailResult, nameResult] = await Promise.all([
    explicitId ? findCustomerByExplicitId(db, businessId, explicitId).catch((error) => {
      console.warn("[customerCrm] explicit customer lookup failed", error);
      return null;
    }) : Promise.resolve(null),
    uid ? findCustomerByUid(customersCol, uid).then((customer) => ({
      customer,
      conflict: null,
      candidates: customer ? [customer] : [],
    })).catch((error) => {
      console.warn("[customerCrm] uid customer lookup failed", error);
      return { customer: null, conflict: null, candidates: [] };
    }) : Promise.resolve({ customer: null, conflict: null, candidates: [] }),
    phoneNormalized ? findCustomerFieldResultR29(customersCol, "phoneNormalized", phoneNormalized, 25).catch((error) => {
      console.warn("[customerCrm] phone customer lookup failed", error);
      return {
        customer: null,
        candidates: [],
        conflict: {
          type: "customer_phone_lookup_failed",
          message: "No pudimos verificar todos los perfiles con este teléfono. Intenta nuevamente antes de crear otro cliente.",
        },
      };
    }) : Promise.resolve({ customer: null, conflict: null, candidates: [] }),
    emailNormalized ? findCustomerFieldResultR29(customersCol, "emailNormalized", emailNormalized, 25).catch((error) => {
      console.warn("[customerCrm] email customer lookup failed", error);
      return {
        customer: null,
        candidates: [],
        conflict: {
          type: "customer_email_lookup_failed",
          message: "No pudimos verificar todos los perfiles con este email. Intenta nuevamente antes de crear otro cliente.",
        },
      };
    }) : Promise.resolve({ customer: null, conflict: null, candidates: [] }),
    (!phoneNormalized && !emailNormalized && !uid && nameKey)
      ? findCompatibleCustomerByNameKeyR29(customersCol, nameKey).catch((error) => {
        console.warn("[customerCrm] nameKey customer lookup failed", error);
        return {
          customer: null,
          conflict: {
            type: "customer_name_lookup_failed",
            message: "No pudimos confirmar si este cliente ya existe. Intenta nuevamente antes de crear otro perfil.",
          },
        };
      })
      : Promise.resolve({ customer: null, conflict: null }),
  ]);

  return {
    idCustomer,
    uidCustomer: uidResult?.customer || null,
    phoneCustomer: phoneResult?.customer || null,
    emailCustomer: emailResult?.customer || null,
    nameCustomer: nameResult?.customer || null,
    fieldConflict: uidResult?.conflict || phoneResult?.conflict || emailResult?.conflict || null,
    nameConflict: nameResult?.conflict || null,
  };
}

function sameCustomerRecord(a, b) {
  if (!a || !b) return true;
  if (a.id && b.id && String(a.id) === String(b.id)) return true;
  const aUid = firstNonEmpty(a.linkedUserUid, a.linkedUserId, a.userUid, a.userId);
  const bUid = firstNonEmpty(b.linkedUserUid, b.linkedUserId, b.userUid, b.userId);
  if (aUid && bUid && aUid === bUid) return true;
  return false;
}

async function queryPlatformCollectionByField(db, collectionName, field, value, matchedBy) {
  if (!value) return null;
  try {
    if (isIosCapacitor()) {
      const rows = await fsClient.listCollectionByPath(collectionName, {
        where: [{ field, op: "EQUAL", value }],
        limit: 1,
      }, { db });
      const row = Array.isArray(rows) && rows.length ? rows[0] : null;
      if (!row) return null;
      const snapLike = { id: row.id, exists: () => true, data: () => row || {} };
      return platformUserIdentityFromDoc(snapLike, matchedBy, collectionName);
    }

    const snap = await getDocs(query(collection(db, collectionName), where(field, "==", value), limit(1)));
    if (snap.empty) return null;
    return platformUserIdentityFromDoc(snap.docs[0], matchedBy, collectionName);
  } catch (error) {
    console.warn(`[customerCrm] ${collectionName}.${field} lookup skipped`, error);
    return null;
  }
}


function platformUserIdentityFromCallable(data = {}, fallbackMatchedBy = "cloud") {
  const source = data.platformUser || data.user || data.profile || data || {};
  const uid = String(source.uid || source.userUid || source.userId || source.id || "").trim();
  if (!uid) return null;

  const email = normalizeEmail(firstNonEmpty(
    source.emailNormalized,
    source.contactEmailNormalized,
    source.authEmailNormalized,
    source.email,
    source.authEmail,
    source.contactEmail,
    source.userEmail
  ));

  const legacyContactPhone = normalizePhone(firstNonEmpty(
    source.profileContactPhone,
    source.phoneE164,
    source.phoneNormalized,
    source.telefonoNormalized,
    source.phone,
    source.telefono,
    source.phoneDisplay,
    source.whatsapp
  ));
  const verifiedPhone = source.phoneVerified === true
    ? normalizePhone(source.phoneVerifiedE164 || "")
    : "";

  return {
    uid,
    id: uid,
    userUid: uid,
    userId: uid,
    linkedUserUid: uid,
    linkedUserId: uid,
    name: platformUserName(source, email || verifiedPhone || "Cliente"),
    email,
    emailNormalized: email,
    phone: verifiedPhone,
    phoneNormalized: verifiedPhone,
    phoneE164: verifiedPhone,
    phoneDisplay: verifiedPhone ? (source.phoneDisplay || verifiedPhone) : "",
    phoneVerified: Boolean(verifiedPhone),
    phoneVerifiedE164: verifiedPhone || "",
    profileContactPhone: legacyContactPhone || "",
    hasProfilePhone: Boolean(verifiedPhone),
    isRealLoopUser: true,
    matchedBy: source.matchedBy || data.matchedBy || fallbackMatchedBy,
    sourceCollection: "users",
    source: "platform_user_match",
    sourceField: source.sourceField || data.sourceField || null,
    resolvedViaCloudFunction: true,
    raw: source.raw || null,
  };
}

async function readPlatformUserIdentityByUid(db, uid = "") {
  const cleanUid = firstNonEmpty(uid);
  if (!db || !cleanUid) return null;

  try {
    if (isIosCapacitor()) {
      const row = await fsClient.getDocByPath(`users/${cleanUid}`);
      if (!row) return null;
      const snapLike = {
        id: cleanUid,
        exists: () => true,
        data: () => row || {},
      };
      return platformUserIdentityFromDoc(snapLike, "uid", "users");
    }

    const snap = await getDoc(doc(db, "users", cleanUid));
    return snap.exists()
      ? platformUserIdentityFromDoc(snap, "uid", "users")
      : null;
  } catch (error) {
    console.warn("[customerCrm] exact UID platform lookup failed", cleanUid, error);
    return null;
  }
}

async function findPlatformUserIdentityMatchesViaCallable({ uid, phoneNormalized, emailNormalized, businessId } = {}) {
  const cleanUid = firstNonEmpty(uid);
  const phone = normalizePhone(phoneNormalized || "");
  const email = normalizeEmail(emailNormalized || "");
  if (!cleanUid && !phone && !email) return null;
  try {
    const callable = httpsCallable(getFunctions(), "resolveLoopCustomerIdentity");
    const result = await callable({ businessId: String(businessId || ""), uid: cleanUid || null, phone, email });
    const data = result && result.data ? result.data : {};
    if (data && data.ok === false) return null;
    return {
      uidUser: data.uidUser ? platformUserIdentityFromCallable(data.uidUser, "uid") : null,
      phoneUser: data.phoneUser ? platformUserIdentityFromCallable(data.phoneUser, "verified_phone") : null,
      emailUser: data.emailUser ? platformUserIdentityFromCallable(data.emailUser, "email") : null,
      viaCloudFunction: true,
    };
  } catch (error) {
    console.warn("[customerCrm] resolveLoopCustomerIdentity callable failed; falling back to local lookup", error?.code || error?.message || error);
    return null;
  }
}

async function findPlatformUserIdentityMatches(db, { linkedUserUid, linkedUserId, userUid, userId, phoneNormalized, emailNormalized, businessId } = {}) {
  if (!db) return { uidUser: null, phoneUser: null, emailUser: null };

  const requestedUid = firstNonEmpty(linkedUserUid, linkedUserId, userUid, userId);
  const phone = normalizePhone(phoneNormalized || "");
  const email = normalizeEmail(emailNormalized || "");

  if (!requestedUid && !phone && !email) {
    return { uidUser: null, phoneUser: null, emailUser: null };
  }

  const cloudResult = await findPlatformUserIdentityMatchesViaCallable({
    uid: requestedUid,
    phoneNormalized: phone,
    emailNormalized: email,
    businessId,
  });

  if (
    cloudResult &&
    (
      cloudResult.uidUser ||
      cloudResult.phoneUser ||
      cloudResult.emailUser ||
      cloudResult.viaCloudFunction
    )
  ) {
    return {
      uidUser: cloudResult.uidUser || null,
      phoneUser: cloudResult.phoneUser || null,
      emailUser: cloudResult.emailUser || null,
    };
  }

  // R35: if a canonical UID is already attached to the booking/customer,
  // resolve that exact account and stop. Provider-specific email/phone data
  // must never redirect a known Firebase UID.
  if (requestedUid) {
    const uidUser = await readPlatformUserIdentityByUid(db, requestedUid);
    return { uidUser, phoneUser: null, emailUser: null };
  }

  const collectionsToSearch = ["users"];
  const emailFields = ["emailNormalized", "contactEmailNormalized", "authEmailNormalized", "email", "authEmail", "contactEmail", "userEmail"];
  // R34: only the verified-phone field is allowed to resolve a Loop user.
  const phoneFields = ["phoneVerifiedE164"];

  const findOne = async (fields, value, matchedBy) => {
    if (!value) return null;
    for (const colName of collectionsToSearch) {
      for (const field of fields) {
        const found = await queryPlatformCollectionByField(db, colName, field, value, matchedBy);
        if (found) return found;
      }
    }
    return null;
  };

  // Without a UID, email is a discovery fallback. Verified phone is last.
  // Neither is allowed to override a known Firebase UID.
  const emailUser = await findOne(emailFields, email, "email");
  const phoneUser = emailUser ? null : await findOne(phoneFields, phone, "verified_phone");

  return { uidUser: null, phoneUser, emailUser };
}

function samePlatformUser(a, b) {
  if (!a || !b) return true;
  const aUid = firstNonEmpty(a.uid, a.userUid, a.userId, a.id);
  const bUid = firstNonEmpty(b.uid, b.userUid, b.userId, b.id);
  return Boolean(aUid && bUid && aUid === bUid);
}

function platformUserPatch(platformUser = {}) {
  const uid = firstNonEmpty(platformUser.uid, platformUser.userUid, platformUser.userId, platformUser.id);
  if (!uid) return {};
  const verifiedPhone = canonicalPlatformPhone(platformUser);
  return {
    hasUserAccount: platformUser.sourceCollection === "users",
    linkedUserId: platformUser.sourceCollection === "users" ? uid : null,
    linkedUserUid: platformUser.sourceCollection === "users" ? uid : null,
    userId: platformUser.sourceCollection === "users" ? uid : null,
    userUid: platformUser.sourceCollection === "users" ? uid : null,
    platformUserId: platformUser.sourceCollection === "users" ? uid : null,
    platformUserUid: platformUser.sourceCollection === "users" ? uid : null,
    platformUserEmail: platformUser.emailNormalized || platformUser.email || null,
    platformUserPhone: verifiedPhone || null,
    phoneVerified: Boolean(verifiedPhone),
    phoneVerifiedE164: verifiedPhone || null,
    customerPhoneVerifiedE164: verifiedPhone || null,
    phoneIdentityVerified: Boolean(verifiedPhone),
    phoneIdentityOwnerUid: verifiedPhone ? uid : null,
    identitySource: platformUser.source || "platform_user_match",
    identityMatchedBy: platformUser.matchedBy || null,
    identitySourceCollection: platformUser.sourceCollection || "users",
    identityUpdatedAt: serverTimestamp(),
  };
}

function isCanonicalLoopUser(platformUser = null) {
  return Boolean(platformUser && platformUser.sourceCollection === "users" && firstNonEmpty(platformUser.uid, platformUser.userUid, platformUser.userId, platformUser.id));
}

function canonicalPlatformPhone(platformUser = null) {
  if (!isCanonicalLoopUser(platformUser)) return "";
  if (platformUser.phoneVerified !== true) return "";
  return identityPhone(platformUser.phoneVerifiedE164 || "");
}

function canonicalPlatformEmail(platformUser = null) {
  if (!isCanonicalLoopUser(platformUser)) return "";
  return identityEmail(platformUser.emailNormalized || platformUser.email || "");
}

function canonicalPlatformName(platformUser = null) {
  if (!isCanonicalLoopUser(platformUser)) return "";
  return firstNonEmpty(platformUser.name, platformUser.displayName, platformUser.email, "Cliente");
}


function identityPhone(value) {
  return normalizePhone(value || "");
}

function identityEmail(value) {
  return normalizeEmail(value || "");
}

function identityContactConflicts({ payload = {}, existingCustomer = null, platformUser = null } = {}) {
  const conflicts = [];
  const typedPhone = identityPhone(payload.phoneNormalized || payload.phone || payload.customerPhone || "");
  const typedEmail = identityEmail(payload.emailNormalized || payload.email || payload.customerEmail || "");
  const existingPhone = identityPhone(existingCustomer?.phoneNormalized || existingCustomer?.phone || existingCustomer?.whatsapp || existingCustomer?.whatsApp || "");
  const existingEmail = identityEmail(existingCustomer?.emailNormalized || existingCustomer?.email || existingCustomer?.customerEmail || "");
  const platformPhone = canonicalPlatformPhone(platformUser);
  const platformEmail = identityEmail(platformUser?.emailNormalized || platformUser?.email || "");

  if (existingCustomer && typedPhone && existingPhone && typedPhone !== existingPhone) {
    conflicts.push({
      field: "phone",
      scope: "business_customer",
      label: "WhatsApp",
      typed: typedPhone,
      existing: existingPhone,
      message: "El cliente existe, pero el WhatsApp guardado es diferente.",
      recommendedAction: "update_existing_customer_phone",
    });
  }
  if (existingCustomer && typedEmail && existingEmail && typedEmail !== existingEmail) {
    conflicts.push({
      field: "email",
      scope: "business_customer",
      label: "Email",
      typed: typedEmail,
      existing: existingEmail,
      message: "El cliente existe, pero el email guardado es diferente.",
      recommendedAction: "update_existing_customer_email",
    });
  }
  if (platformUser && typedPhone && platformPhone && typedPhone !== platformPhone) {
    conflicts.push({
      field: "phone",
      scope: "platform_user",
      label: "WhatsApp de cuenta Loop",
      typed: typedPhone,
      existing: platformPhone,
      message: "La cuenta Loop tiene otro WhatsApp guardado.",
      recommendedAction: "flag_platform_phone_sync",
    });
  }
  if (platformUser && typedEmail && platformEmail && typedEmail !== platformEmail) {
    conflicts.push({
      field: "email",
      scope: "platform_user",
      label: "Email de cuenta Loop",
      typed: typedEmail,
      existing: platformEmail,
      message: "La cuenta Loop tiene otro email guardado.",
      recommendedAction: "flag_platform_email_sync",
    });
  }
  return conflicts;
}

function buildResolvedCustomerSnapshot({ payload, existingCustomer = null, platformUser = null, status = "new_customer" } = {}) {
  const hasCanonicalLoopUser = isCanonicalLoopUser(platformUser);
  const linkedUid = firstNonEmpty(
    platformUser?.uid,
    platformUser?.userUid,
    platformUser?.userId,
    existingCustomer?.linkedUserUid,
    existingCustomer?.linkedUserId,
    existingCustomer?.userUid,
    existingCustomer?.userId,
    existingCustomer?.platformUserUid,
    existingCustomer?.platformUserId
  );
  const optOut = existingCustomer?.whatsappOptOut === true || existingCustomer?.whatsappOptIn === false || existingCustomer?.customerWhatsappConsent === false;
  const fieldConflicts = identityContactConflicts({ payload, existingCustomer, platformUser });
  const typedPhone = identityPhone(payload?.phoneNormalized || payload?.phone || payload?.customerPhone || "");
  const typedEmail = identityEmail(payload?.emailNormalized || payload?.email || payload?.customerEmail || "");
  const platformPhone = canonicalPlatformPhone(platformUser);
  const platformEmail = canonicalPlatformEmail(platformUser);
  const platformName = canonicalPlatformName(platformUser);

  const existingPhone = identityPhone(existingCustomer?.phoneNormalized || existingCustomer?.phoneE164 || existingCustomer?.phone || existingCustomer?.whatsapp || existingCustomer?.whatsApp || "");
  const existingEmail = identityEmail(existingCustomer?.emailNormalized || existingCustomer?.email || existingCustomer?.customerEmail || "");

  const canonicalOverrides = [];
  if (hasCanonicalLoopUser) {
    if (typedPhone && platformPhone && typedPhone !== platformPhone) {
      canonicalOverrides.push({ field: "phone", scope: "typed_input", label: "WhatsApp", typed: typedPhone, canonical: platformPhone, message: "Se usará el WhatsApp del perfil Loop." });
    }
    if (existingPhone && platformPhone && existingPhone !== platformPhone) {
      canonicalOverrides.push({ field: "phone", scope: "business_customer", label: "WhatsApp", typed: platformPhone, existing: existingPhone, message: "El cliente del negocio se sincronizará con el WhatsApp del perfil Loop." });
    }
    if (typedEmail && platformEmail && typedEmail !== platformEmail) {
      canonicalOverrides.push({ field: "email", scope: "typed_input", label: "Email", typed: typedEmail, canonical: platformEmail, message: "Se usará el email del perfil Loop." });
    }
    if (existingEmail && platformEmail && existingEmail !== platformEmail) {
      canonicalOverrides.push({ field: "email", scope: "business_customer", label: "Email", typed: platformEmail, existing: existingEmail, message: "El cliente del negocio se sincronizará con el email del perfil Loop." });
    }
  }

  const resolvedPhone = hasCanonicalLoopUser
    ? firstNonEmpty(
        platformPhone,
        existingCustomer?.phoneNormalized,
        existingCustomer?.phoneE164,
        existingCustomer?.phone,
        existingCustomer?.whatsapp,
        existingCustomer?.whatsApp,
        typedPhone
      )
    : existingCustomer
      ? firstNonEmpty(existingCustomer?.phoneNormalized, existingCustomer?.phoneE164, existingCustomer?.phone, existingCustomer?.whatsapp, existingCustomer?.whatsApp, typedPhone, platformUser?.phoneNormalized, platformUser?.phoneE164, platformUser?.phone)
      : firstNonEmpty(typedPhone, platformUser?.phoneNormalized, platformUser?.phoneE164, platformUser?.phone);
  const resolvedEmail = hasCanonicalLoopUser
    ? platformEmail
    : existingCustomer
      ? firstNonEmpty(existingCustomer?.emailNormalized, existingCustomer?.email, existingCustomer?.customerEmail, typedEmail, platformUser?.emailNormalized, platformUser?.email)
      : firstNonEmpty(typedEmail, platformUser?.emailNormalized, platformUser?.email);
  const resolvedName = hasCanonicalLoopUser
    ? platformName
    : existingCustomer
      ? firstNonEmpty(existingCustomer?.name, existingCustomer?.customerName, payload?.name, payload?.customerName, platformUser?.name, "Cliente")
      : firstNonEmpty(payload?.name, payload?.customerName, platformUser?.name, "Cliente");

  // V4.4: for canonical Loop profiles, only show the canonical sync changes.
  // Typed/manual conflicts are not actionable because ProfilePage is the source of truth.
  const allFieldConflicts = hasCanonicalLoopUser
    ? canonicalOverrides
    : fieldConflicts;

  return {
    id: existingCustomer?.id || null,
    customerId: existingCustomer?.id || null,
    name: resolvedName,
    customerName: resolvedName,
    displayName: resolvedName,
    phone: resolvedPhone,
    phoneNormalized: identityPhone(resolvedPhone),
    phoneE164: identityPhone(resolvedPhone),
    customerPhoneNormalized: identityPhone(resolvedPhone),
    whatsapp: resolvedPhone,
    whatsApp: resolvedPhone,
    phoneVerified: Boolean(platformPhone),
    phoneVerifiedE164: platformPhone || null,
    customerPhoneVerifiedE164: platformPhone || null,
    phoneIdentityVerified: Boolean(platformPhone),
    phoneIdentityOwnerUid: platformPhone && linkedUid ? linkedUid : null,
    email: resolvedEmail,
    emailNormalized: identityEmail(resolvedEmail),
    customerEmail: identityEmail(resolvedEmail),
    hasUserAccount: Boolean(linkedUid),
    linkedUserId: linkedUid || null,
    linkedUserUid: linkedUid || null,
    userId: linkedUid || null,
    userUid: linkedUid || null,
    platformUserId: linkedUid || null,
    platformUserUid: linkedUid || null,
    platformUserEmail: platformEmail || platformUser?.emailNormalized || platformUser?.email || existingCustomer?.platformUserEmail || null,
    platformUserPhone: platformPhone || existingCustomer?.platformUserPhone || null,
    identitySource: platformUser?.source || existingCustomer?.identitySource || (linkedUid ? "platform_user_match" : null),
    identityMatchedBy: platformUser?.matchedBy || existingCustomer?.identityMatchedBy || null,
    identitySourceCollection: platformUser?.sourceCollection || existingCustomer?.identitySourceCollection || null,
    identityContactSource: hasCanonicalLoopUser
      ? (platformPhone ? "platform_user_verified_phone" : "business_customer_or_typed_contact")
      : "business_customer_or_typed",
    identityCanonicalProfile: hasCanonicalLoopUser,
    identityStatus: status,
    identityFieldConflicts: allFieldConflicts,
    identityHasFieldConflict: allFieldConflicts.length > 0,
    identityNeedsUserProfileSync: false,
    identityConflictResolvedByBusiness: false,
    source: existingCustomer?.source || platformUser?.source || payload?.source || "manual",
    totalBookings: Number(existingCustomer?.totalBookings || existingCustomer?.importedTotalBookings || 0),
    importedTotalBookings: Number(existingCustomer?.importedTotalBookings || existingCustomer?.totalBookings || 0),
    lastBookingAt: existingCustomer?.lastBookingAt || null,
    importedLastBookingAtText: existingCustomer?.importedLastBookingAtText || "",
    whatsappOptOut: optOut,
    whatsappOptIn: !optOut,
    customerWhatsappConsent: !optOut,
    whatsappPhoneStatus: whatsappPhoneStatus(resolvedPhone) || existingCustomer?.whatsappPhoneStatus || payload?.whatsappPhoneStatus || "unknown",
  };
}

export async function resolveCustomerIdentityForBooking(db, businessId, rawCustomer = {}, options = {}) {
  const payload = customerPayloadFromBooking(rawCustomer);
  const emptyResult = {
    status: "new_customer",
    payload,
    existingCustomer: null,
    platformUser: null,
    customerId: null,
    customerSnapshot: null,
    conflict: null,
  };
  if (!db || !businessId) return emptyResult;

  const { idCustomer, uidCustomer, phoneCustomer, emailCustomer, nameCustomer, fieldConflict, nameConflict } = await findCustomerIdentityMatches(db, businessId, payload);
  const trustExplicitCustomerId = Boolean(options.trustExplicitCustomerId === true && idCustomer);
  const requestedUid = firstNonEmpty(
    payload.linkedUserUid,
    payload.linkedUserId,
    payload.userUid,
    payload.userId
  );

  // R34 identity precedence:
  // UID > email > verified phone/contact phone. A duplicated contact phone must
  // not block or redirect a customer already identified by UID/email.
  const strongBusinessCustomer = uidCustomer || emailCustomer || null;
  const fieldConflictField = String(fieldConflict?.field || "").toLowerCase();
  const fieldConflictType = String(fieldConflict?.type || "").toLowerCase();
  const fieldConflictIsPhone =
    fieldConflictField.includes("phone") ||
    fieldConflictField.includes("telefono") ||
    fieldConflictField.includes("whatsapp") ||
    fieldConflictType.includes("phone");

  const ignorableFieldConflict = Boolean(
    fieldConflict &&
    !trustExplicitCustomerId &&
    strongBusinessCustomer &&
    fieldConflictIsPhone
  );

  // An existing customer selected by exact document id should not be rejected
  // because a secondary lookup is ambiguous. Likewise a phone-only conflict is
  // informational once UID/email already identified the CRM customer.
  if (fieldConflict && !trustExplicitCustomerId && !ignorableFieldConflict) {
    return {
      ...emptyResult,
      status: "conflict",
      conflict: fieldConflict,
      customerSnapshot: null,
    };
  }

  if (nameConflict && !idCustomer) {
    return {
      ...emptyResult,
      status: "conflict",
      conflict: nameConflict,
      customerSnapshot: null,
    };
  }

  // R34: shared contact phones are legal; they are not account identity.
  // A UID match wins. If no UID match exists, email wins. Phone is only a
  // fallback for an offline/unlinked contact.
  const strongCustomer = uidCustomer || emailCustomer || null;
  const contactCustomer = strongCustomer || phoneCustomer || null;

  // UID and email disagreeing is a real identity conflict. UID vs phone is not:
  // the phone may simply be a shared/offline WhatsApp contact.
  const uidEmailConflict =
    uidCustomer &&
    emailCustomer &&
    !sameCustomerRecord(uidCustomer, emailCustomer);

  if (uidEmailConflict && !trustExplicitCustomerId) {
    return {
      ...emptyResult,
      status: "conflict",
      conflict: {
        type: "business_customer_uid_email_conflict",
        message: "La cuenta Loop y el email apuntan a clientes diferentes de este negocio.",
        idCustomer,
        uidCustomer,
        phoneCustomer,
        emailCustomer,
      },
      customerSnapshot: null,
    };
  }

  const preferredMatchedCustomer =
    uidCustomer ||
    emailCustomer ||
    phoneCustomer ||
    null;

  const explicitCustomerSuperseded = Boolean(
    !trustExplicitCustomerId &&
    idCustomer &&
    preferredMatchedCustomer &&
    !sameCustomerRecord(idCustomer, preferredMatchedCustomer)
  );

  const existingCustomer = trustExplicitCustomerId
    ? idCustomer
    : (preferredMatchedCustomer || idCustomer || nameCustomer || null);
  const platformMatches = options.linkPlatformUser === false
    ? { phoneUser: null, emailUser: null }
    : await findPlatformUserIdentityMatches(db, { ...payload, businessId }).catch((error) => {
      console.warn("[customerCrm] platform user identity lookup failed", error);
      return { uidUser: null, phoneUser: null, emailUser: null };
    });

  const { uidUser, phoneUser, emailUser } = platformMatches || {};

  // LOOP BOOKING IDENTITY HOTFIX V30C
  // Defer duplicated global Loop profiles when the business already selected
  // an exact CRM customer. Do not automatically link either ambiguous user.
  if (
    trustExplicitCustomerId &&
    existingCustomer &&
    phoneUser &&
    emailUser &&
    !samePlatformUser(phoneUser, emailUser)
  ) {
    const customerSnapshot = buildResolvedCustomerSnapshot({
      payload,
      existingCustomer,
      platformUser: null,
      status: "existing_customer",
    });

    return {
      status: "existing_customer",
      payload,
      existingCustomer,
      platformUser: null,
      customerId: existingCustomer.id || null,
      customerSnapshot,
      fieldConflicts: [],
      recommendedAction:
        "use_selected_business_customer_platform_conflict_deferred",
      explicitCustomerSuperseded: false,
      conflict: null,
      identityWarning: {
        type: "platform_user_conflict_deferred",
        message:
          "El cliente seleccionado puede reservar. La identidad Loop global requiere una revisión separada.",
      },
    };
  }

  if (phoneUser && emailUser && !samePlatformUser(phoneUser, emailUser)) {
    return {
      ...emptyResult,
      status: "conflict",
      existingCustomer,
      conflict: {
        type: "platform_user_conflict",
        message: "El WhatsApp y el email pertenecen a usuarios Loop diferentes.",
        phoneUser,
        emailUser,
      },
      customerSnapshot: existingCustomer ? buildResolvedCustomerSnapshot({ payload, existingCustomer, status: "existing_customer" }) : null,
    };
  }

  const platformUser = uidUser || emailUser || phoneUser || null;
  const hasRealLoopUser = isCanonicalLoopUser(platformUser);
  const profilePhone = canonicalPlatformPhone(platformUser);

  const platformUid = firstNonEmpty(
    platformUser?.uid,
    platformUser?.userUid,
    platformUser?.userId,
    platformUser?.id
  );
  const existingUid = firstNonEmpty(
    existingCustomer?.linkedUserUid,
    existingCustomer?.linkedUserId,
    existingCustomer?.userUid,
    existingCustomer?.userId,
    existingCustomer?.platformUserUid,
    existingCustomer?.platformUserId
  );

  if (
    platformUid &&
    existingUid &&
    platformUid !== existingUid &&
    !trustExplicitCustomerId
  ) {
    return {
      ...emptyResult,
      status: "conflict",
      existingCustomer,
      platformUser,
      conflict: {
        type: "business_customer_platform_uid_conflict",
        message: "Este cliente ya está vinculado a otra cuenta Loop. Selecciona el perfil correcto antes de continuar.",
        existingUid,
        platformUid,
      },
      customerSnapshot: null,
    };
  }

  if (existingCustomer) {
    const customerSnapshot = buildResolvedCustomerSnapshot({ payload, existingCustomer, platformUser, status: "existing_customer" });
    const canonical = customerSnapshot.identityCanonicalProfile === true;
    return {
      status: "existing_customer",
      payload,
      existingCustomer,
      platformUser,
      customerId: existingCustomer.id || null,
      customerSnapshot,
      fieldConflicts: customerSnapshot.identityFieldConflicts || [],
      recommendedAction: explicitCustomerSuperseded ? "use_contact_matched_customer" : (canonical ? "sync_existing_customer_to_platform_profile" : (customerSnapshot.identityHasFieldConflict ? "update_existing_customer_contact" : "use_existing_customer")),
      explicitCustomerSuperseded,
      conflict: null,
    };
  }

  if (platformUser) {
    const customerSnapshot = buildResolvedCustomerSnapshot({ payload, platformUser, status: "platform_user" });
    const canonical = customerSnapshot.identityCanonicalProfile === true;
    return {
      status: "platform_user",
      payload,
      existingCustomer: null,
      platformUser,
      customerId: null,
      customerSnapshot,
      fieldConflicts: customerSnapshot.identityFieldConflicts || [],
      recommendedAction: canonical ? "link_platform_profile" : (customerSnapshot.identityHasFieldConflict ? "link_platform_user_keep_typed_contact" : "link_platform_user"),
      conflict: null,
    };
  }

  return emptyResult;
}

async function findCustomerByPhoneOrEmail(db, businessId, { id, customerId, clientId, linkedUserUid, linkedUserId, userUid, userId, phoneNormalized, emailNormalized, nameKey } = {}) {
  const matches = await findCustomerIdentityMatches(db, businessId, {
    id,
    customerId,
    clientId,
    linkedUserUid,
    linkedUserId,
    userUid,
    userId,
    phoneNormalized,
    emailNormalized,
    nameKey,
  });
  if (matches.fieldConflict) {
    const err = new Error(matches.fieldConflict.message || "customer_field_identity_conflict");
    err.code = "customer_field_identity_conflict";
    err.conflict = matches.fieldConflict;
    throw err;
  }
  if (matches.nameConflict && !matches.idCustomer) {
    const err = new Error(matches.nameConflict.message || "customer_name_identity_conflict");
    err.code = "customer_name_identity_conflict";
    err.conflict = matches.nameConflict;
    throw err;
  }
  return matches.idCustomer || matches.uidCustomer || matches.phoneCustomer || matches.emailCustomer || matches.nameCustomer || null;
}


function customerCrmTimestampValue() {
  return isIosCapacitor() ? new Date().toISOString() : serverTimestamp();
}

function customerCrmTotalBookingsValue(existing = {}, incrementBy = 0, importedTotal = 0) {
  if (!isIosCapacitor()) return increment(incrementBy);
  const current = Number(existing.totalBookings ?? existing.importedTotalBookings ?? importedTotal ?? 0);
  const safeCurrent = Number.isFinite(current) ? current : 0;
  const safeIncrement = Number.isFinite(Number(incrementBy)) ? Number(incrementBy) : 0;
  return Math.max(0, safeCurrent + safeIncrement);
}

async function customerCrmUpdateCustomerRecord(db, businessId, customerId, data) {
  const cleanBusinessId = String(businessId || "").trim();
  const cleanCustomerId = String(customerId || "").trim();
  if (!cleanBusinessId || !cleanCustomerId) {
    const err = new Error("customer_record_missing_document_id");
    err.code = "customer_record_missing_document_id";
    throw err;
  }
  const payload = {
    ...(data || {}),
    __documentId: cleanCustomerId,
    id: cleanCustomerId,
    customerId: cleanCustomerId,
    clientId: cleanCustomerId,
  };
  if (isIosCapacitor()) {
    return fsClient.updateDocByPath(`businesses/${cleanBusinessId}/customers/${cleanCustomerId}`, payload, { db });
  }
  return updateDoc(doc(db, `businesses/${cleanBusinessId}/customers`, cleanCustomerId), payload);
}

async function customerCrmAddCustomerRecord(db, businessId, data) {
  const cleanBusinessId = String(businessId || "").trim();
  if (!cleanBusinessId) throw new Error("missing_business_id_for_customer_create");

  // Preallocate the Firestore id and write it into the record. This prevents
  // future reads from seeing `id: null` even if an older payload carried null ids.
  const createdRef = doc(collection(db, `businesses/${cleanBusinessId}/customers`));
  const id = String(createdRef.id || "").trim();
  const payload = {
    ...(data || {}),
    __documentId: id,
    id,
    customerId: id,
    clientId: id,
  };

  if (isIosCapacitor()) {
    await fsClient.setDocByPath(`businesses/${cleanBusinessId}/customers/${id}`, payload, { db, merge: false });
  } else {
    await setDoc(createdRef, payload);
  }
  return payload;
}

export async function findOrCreateCustomerForBusiness(db, businessId, rawCustomer = {}, options = {}) {
  if (!db || !businessId) throw new Error("missing_db_or_businessId");

  let resolved = null;
  try {
    resolved = await resolveCustomerIdentityForBooking(db, businessId, rawCustomer, options);
  } catch (error) {
    console.warn("[customerCrm] identity resolution failed closed to prevent a duplicate", error);
    const err = new Error(
      error?.message ||
      "No pudimos verificar si este cliente ya existe. Intenta nuevamente antes de crear otro perfil."
    );
    err.code = error?.code || "customer_identity_lookup_failed";
    err.conflict = error?.conflict || null;
    throw err;
  }

  if (resolved?.status === "conflict" && options.allowIdentityConflict !== true) {
    const err = new Error(resolved.conflict?.message || "customer_identity_conflict");
    err.code = "customer_identity_conflict";
    err.conflict = resolved.conflict;
    throw err;
  }

  const platformUser = resolved?.platformUser || null;
  const hasCanonicalLoopUser = isCanonicalLoopUser(platformUser);
  const profilePhone = canonicalPlatformPhone(platformUser);

  const rawWithPlatformUser = platformUser ? {
    ...rawCustomer,
    name: hasCanonicalLoopUser ? (canonicalPlatformName(platformUser) || rawCustomer.name || rawCustomer.customerName) : (rawCustomer.name || rawCustomer.customerName || platformUser.name),
    customerName: hasCanonicalLoopUser ? (canonicalPlatformName(platformUser) || rawCustomer.customerName || rawCustomer.name) : (rawCustomer.customerName || rawCustomer.name || platformUser.name),
    customerEmail: hasCanonicalLoopUser ? (canonicalPlatformEmail(platformUser) || rawCustomer.customerEmail || rawCustomer.email || "") : (rawCustomer.customerEmail || rawCustomer.email || platformUser.email || ""),
    email: hasCanonicalLoopUser ? (canonicalPlatformEmail(platformUser) || rawCustomer.email || rawCustomer.customerEmail || "") : (rawCustomer.email || rawCustomer.customerEmail || platformUser.email || ""),
    customerPhone: hasCanonicalLoopUser
      ? (profilePhone || rawCustomer.customerPhone || rawCustomer.phone || "")
      : (rawCustomer.customerPhone || rawCustomer.phone || platformUser.phone || ""),
    phone: hasCanonicalLoopUser
      ? (profilePhone || rawCustomer.phone || rawCustomer.customerPhone || "")
      : (rawCustomer.phone || rawCustomer.customerPhone || platformUser.phone || ""),
    ...platformUserPatch(platformUser),
  } : rawCustomer;

  const payload = customerPayloadFromBooking(rawWithPlatformUser);
  const existingRaw = resolved?.existingCustomer || await findCustomerByPhoneOrEmail(db, businessId, payload);
  const existing = existingRaw ? canonicalCustomerRecord(existingRaw) : null;

  const importedTotal = Number(payload.importedTotalBookings || 0);
  const incrementBy = options.incrementBookings === false ? 0 : 1;

  if (existing) {
    const snapshotForAudit = buildResolvedCustomerSnapshot({ payload, existingCustomer: existing, platformUser, status: resolved?.status || "existing_customer" });
    const fieldConflicts = snapshotForAudit.identityFieldConflicts || [];
    const needsProfileSync = fieldConflicts.some((item) => item.scope === "platform_user");
    const canonicalVerifiedPhone = hasCanonicalLoopUser ? profilePhone : "";
    const snapshotContactPhone = snapshotForAudit.phoneNormalized || snapshotForAudit.phone || "";
    const canonicalSnapshotEmail = hasCanonicalLoopUser
      ? (snapshotForAudit.emailNormalized || snapshotForAudit.email || canonicalPlatformEmail(platformUser) || "")
      : "";

    const nextPhone =
      canonicalVerifiedPhone ||
      snapshotContactPhone ||
      existing.phoneNormalized ||
      existing.phoneE164 ||
      existing.phone ||
      payload.phoneNormalized ||
      payload.phone ||
      "";

    const nextEmail =
      canonicalSnapshotEmail ||
      existing.emailNormalized ||
      existing.email ||
      existing.customerEmail ||
      payload.emailNormalized ||
      payload.email ||
      "";

    const existingUid = customerR29Uid(existing);
    const platformUid = hasCanonicalLoopUser
      ? firstNonEmpty(platformUser?.uid, platformUser?.userUid, platformUser?.userId, platformUser?.id)
      : "";
    const payloadUid = firstNonEmpty(
      payload.linkedUserUid,
      payload.linkedUserId,
      payload.userUid,
      payload.userId
    );

    if (existingUid && platformUid && existingUid !== platformUid) {
      const err = new Error("Este cliente ya está vinculado a otra cuenta Loop. No se modificó la identidad.");
      err.code = "customer_existing_uid_conflict";
      err.conflict = { existingUid, platformUid };
      throw err;
    }

    const finalLinkedUid = platformUid || existingUid || payloadUid || "";
    const existingVerifiedPhone = normalizePhone(
      existing.phoneVerifiedE164 ||
      existing.customerPhoneVerifiedE164 ||
      ""
    );
    const existingVerifiedOwnerUid = String(existing.phoneIdentityOwnerUid || "").trim();
    const canPreserveExistingVerifiedPhone = Boolean(
      existingVerifiedPhone &&
      existing.phoneIdentityVerified === true &&
      (!existingVerifiedOwnerUid || !finalLinkedUid || existingVerifiedOwnerUid === finalLinkedUid)
    );
    const finalVerifiedPhone =
      canonicalVerifiedPhone ||
      (canPreserveExistingVerifiedPhone ? existingVerifiedPhone : "");

    // R34: a linked Loop profile owns its canonical display name. Manual text may
    // rename offline customers, but it may not leave a linked account stuck on "Juan1".
    const existingName = firstNonEmpty(existing.name, existing.customerName, existing.displayName);
    const canonicalProfileName = hasCanonicalLoopUser ? canonicalPlatformName(platformUser) : "";
    const shouldUpdateExistingName =
      Boolean(canonicalProfileName) ||
      options.updateExistingCustomerName === true ||
      !existingName;
    const nextName = canonicalProfileName || (
      shouldUpdateExistingName
        ? (payload.name || existingName || "Cliente")
        : existingName
    );
    const nextNameKey = String(nextName || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const preserveLegacyCrmName = Boolean(
      canonicalProfileName &&
      existingName &&
      existingName.trim() !== canonicalProfileName.trim()
    );
    const existingDocumentId = customerRecordDocumentId(existing);
    if (!existingDocumentId) {
      const err = new Error("Encontramos el cliente, pero falta su identificador interno. Vuelve a seleccionarlo e intenta otra vez.");
      err.code = "customer_record_missing_document_id";
      throw err;
    }
    await customerCrmUpdateCustomerRecord(db, businessId, existingDocumentId, {
      name: nextName,
      customerName: nextName,
      displayName: nextName,
      nameKey: nextNameKey,
      phone: nextPhone,
      phoneNormalized: normalizePhone(nextPhone),
      phoneE164: normalizePhone(nextPhone),
      customerPhoneNormalized: normalizePhone(nextPhone),
      whatsapp: nextPhone,
      whatsApp: nextPhone,
      phoneVerified: Boolean(finalVerifiedPhone),
      phoneVerifiedE164: finalVerifiedPhone || null,
      customerPhoneVerifiedE164: finalVerifiedPhone || null,
      phoneIdentityVerified: Boolean(finalVerifiedPhone),
      phoneIdentityOwnerUid: finalVerifiedPhone && finalLinkedUid ? finalLinkedUid : null,
      email: nextEmail,
      emailNormalized: normalizeEmail(nextEmail),
      customerEmail: normalizeEmail(nextEmail),
      notes: payload.notes || existing.notes || "",
      tags: Array.isArray(payload.tags) && payload.tags.length ? payload.tags : existing.tags || [],
      whatsappOptOut: existing.whatsappOptOut === true || payload.whatsappOptOut === true,
      whatsappOptIn: existing.whatsappOptOut === true ? false : payload.whatsappOptIn !== false,
      customerWhatsappConsent: existing.whatsappOptOut === true ? false : payload.customerWhatsappConsent !== false,
      whatsappPhoneStatus: finalVerifiedPhone ? "verified" : whatsappPhoneStatus(nextPhone),
      source: existing.source || payload.source || "manual",
      legacyCrmName: preserveLegacyCrmName
        ? (existing.legacyCrmName || existingName)
        : (existing.legacyCrmName || null),
      hasUserAccount: Boolean(finalLinkedUid),
      linkedUserId: finalLinkedUid || null,
      linkedUserUid: finalLinkedUid || null,
      userId: finalLinkedUid || null,
      userUid: finalLinkedUid || null,
      platformUserId: finalLinkedUid || null,
      platformUserUid: finalLinkedUid || null,
      platformUserEmail: platformUser?.emailNormalized || platformUser?.email || existing.platformUserEmail || null,
      platformUserPhone: finalVerifiedPhone || existing.platformUserPhone || null,
      identityUpdatedAt: finalLinkedUid ? customerCrmTimestampValue() : existing.identityUpdatedAt || null,
      identitySource: finalLinkedUid ? (payload.identitySource || platformUser?.source || existing.identitySource || "crm_match_linked_user") : existing.identitySource || null,
      identityMatchedBy: payload.identityMatchedBy || platformUser?.matchedBy || existing.identityMatchedBy || null,
      identitySourceCollection: payload.identitySourceCollection || platformUser?.sourceCollection || existing.identitySourceCollection || null,
      identityContactSource: hasCanonicalLoopUser
        ? (finalVerifiedPhone ? "platform_user_verified_phone" : "business_customer_contact")
        : (existing.identityContactSource || null),
      identityCanonicalProfile: hasCanonicalLoopUser,
      identityNeedsUserProfileSync: hasCanonicalLoopUser ? false : (needsProfileSync || existing.identityNeedsUserProfileSync === true),
      identityFieldConflicts: fieldConflicts,
      identityConflictResolvedByBusiness: hasCanonicalLoopUser ? false : (fieldConflicts.length > 0 || existing.identityConflictResolvedByBusiness === true),
      createdByRole: existing.createdByRole || options.createdByRole || rawCustomer.createdByRole || null,
      createdByEmail: existing.createdByEmail || normalizeEmail(options.createdByEmail || rawCustomer.createdByEmail || ""),
      importedTotalBookings: importedTotal || existing.importedTotalBookings || 0,
      importedLastBookingAtText: payload.importedLastBookingAtText || existing.importedLastBookingAtText || "",
      importedRewardPointsText: payload.importedRewardPointsText || existing.importedRewardPointsText || "",
      lastBookingAt: options.bookingDate || existing.lastBookingAt || customerCrmTimestampValue(),
      totalBookings: customerCrmTotalBookingsValue(existing, incrementBy, importedTotal),
      updatedAt: customerCrmTimestampValue(),
    });

    return {
      ...existing,
      ...payload,
      name: nextName,
      customerName: nextName,
      displayName: nextName,
      nameKey: nextNameKey,
      __documentId: existingDocumentId,
      id: existingDocumentId,
      customerId: existingDocumentId,
      clientId: existingDocumentId,
      phone: nextPhone,
      phoneNormalized: normalizePhone(nextPhone),
      phoneE164: normalizePhone(nextPhone),
      phoneVerified: Boolean(finalVerifiedPhone),
      phoneVerifiedE164: finalVerifiedPhone || null,
      customerPhoneVerifiedE164: finalVerifiedPhone || null,
      phoneIdentityVerified: Boolean(finalVerifiedPhone),
      phoneIdentityOwnerUid: finalVerifiedPhone && finalLinkedUid ? finalLinkedUid : null,
      email: nextEmail,
      emailNormalized: normalizeEmail(nextEmail),
      hasUserAccount: Boolean(finalLinkedUid),
      linkedUserId: finalLinkedUid || null,
      linkedUserUid: finalLinkedUid || null,
      userId: finalLinkedUid || null,
      userUid: finalLinkedUid || null,
      platformUserId: finalLinkedUid || null,
      platformUserUid: finalLinkedUid || null,
      legacyCrmName: preserveLegacyCrmName
        ? (existing.legacyCrmName || existingName)
        : (existing.legacyCrmName || null),
      existed: true,
      identityStatus: resolved?.status || "existing_customer",
      identityFieldConflicts: fieldConflicts,
      identityNeedsUserProfileSync: needsProfileSync,
      identitySupersededExplicitCustomerId: resolved?.explicitCustomerSuperseded === true,
    };
  }

  const createdSnapshot = buildResolvedCustomerSnapshot({ payload, platformUser, status: platformUser ? "platform_user" : "new_customer" });
  const createdPhone = createdSnapshot.phoneNormalized || payload.phoneNormalized || payload.phone || "";
  const createdEmail = createdSnapshot.emailNormalized || payload.emailNormalized || payload.email || "";
  const created = {
    ...payload,
    ...createdSnapshot,
    businessId,
    phone: createdPhone,
    phoneNormalized: normalizePhone(createdPhone),
    phoneE164: normalizePhone(createdPhone),
    customerPhoneNormalized: normalizePhone(createdPhone),
    whatsapp: createdPhone,
    whatsApp: createdPhone,
    email: normalizeEmail(createdEmail),
    emailNormalized: normalizeEmail(createdEmail),
    customerEmail: normalizeEmail(createdEmail),
    source: payload.source || "manual",
    createdByRole: options.createdByRole || rawCustomer.createdByRole || null,
    createdByEmail: normalizeEmail(options.createdByEmail || rawCustomer.createdByEmail || ""),
    identityFieldConflicts: createdSnapshot.identityFieldConflicts || [],
    identityNeedsUserProfileSync: createdSnapshot.identityNeedsUserProfileSync === true,
    identityConflictResolvedByBusiness: createdSnapshot.identityHasFieldConflict === true,
    totalBookings: importedTotal || incrementBy,
    lastBookingAt: options.bookingDate || null,
    createdAt: customerCrmTimestampValue(),
    updatedAt: customerCrmTimestampValue(),
  };

  const createdRef = await customerCrmAddCustomerRecord(db, businessId, created);
  return { ...created, id: createdRef.id, customerId: createdRef.id, clientId: createdRef.id, existed: false, identityStatus: platformUser ? "platform_user" : "new_customer" };
}



function importCustomerIdentityKey(customer = {}) {
  const phone = normalizePhone(customer.phoneNormalized || customer.phone || customer.customerPhone || customer.whatsapp || "");
  if (phone) return `phone:${phone}`;
  const email = normalizeEmail(customer.emailNormalized || customer.email || customer.customerEmail || "");
  if (email) return `email:${email}`;
  const nameKey = String(customer.nameKey || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
  return nameKey && nameKey.length >= 3 ? `name:${nameKey}` : "";
}

function importCustomerIdentityKeysR29(customer = {}) {
  const keys = [];
  const phone = normalizePhone(customer.phoneNormalized || customer.phone || customer.customerPhone || customer.whatsapp || "");
  const email = normalizeEmail(customer.emailNormalized || customer.email || customer.customerEmail || "");
  const nameKey = String(customer.nameKey || customer.searchableName || customer.name || customer.customerName || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
  if (phone) keys.push(`phone:${phone}`);
  if (email) keys.push(`email:${email}`);
  if (nameKey && nameKey.length >= 3) keys.push(`name:${nameKey}`);
  return Array.from(new Set(keys));
}

function importCustomerCandidateCompatibleR29(a = {}, b = {}) {
  const rows = [a, b].filter(Boolean);
  return !customerR29IdentityConflict(rows).conflict;
}

function importCustomerBestCandidateR29(a = null, b = null) {
  if (!a) return b;
  if (!b) return a;
  return [a, b].sort(customerR29StableCompare)[0];
}

function buildCustomerSearchTextForImport(customer = {}) {
  return [
    customer.name,
    customer.customerName,
    customer.displayName,
    customer.fullName,
    customer.nameKey,
    customer.phone,
    customer.phoneNormalized,
    customer.email,
    customer.emailNormalized,
    customer.notes,
    customer.customerNotes,
    Array.isArray(customer.tags) ? customer.tags.join(" ") : "",
  ]
    .filter(Boolean)
    .join(" ")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function mergeImportCustomerRecords(primary = {}, next = {}) {
  const first = primary && typeof primary === "object" ? primary : {};
  const second = next && typeof next === "object" ? next : {};
  const tags = Array.from(new Set([...(Array.isArray(first.tags) ? first.tags : []), ...(Array.isArray(second.tags) ? second.tags : [])].map((x) => String(x || "").trim()).filter(Boolean)));
  const notes = [first.notes || first.customerNotes || "", second.notes || second.customerNotes || ""]
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  const uniqueNotes = Array.from(new Set(notes));
  const importedTotalBookings = Math.max(Number(first.importedTotalBookings || first.totalBookings || 0) || 0, Number(second.importedTotalBookings || second.totalBookings || 0) || 0);
  return {
    ...first,
    ...second,
    name: first.name || second.name || second.customerName || first.customerName || "Cliente",
    customerName: first.customerName || first.name || second.customerName || second.name || "Cliente",
    displayName: first.displayName || first.name || second.displayName || second.name || "Cliente",
    phone: first.phoneNormalized || first.phone || second.phoneNormalized || second.phone || "",
    phoneNormalized: first.phoneNormalized || second.phoneNormalized || normalizePhone(first.phone || second.phone || ""),
    phoneE164: first.phoneE164 || first.phoneNormalized || second.phoneE164 || second.phoneNormalized || normalizePhone(first.phone || second.phone || ""),
    email: first.emailNormalized || first.email || second.emailNormalized || second.email || "",
    emailNormalized: first.emailNormalized || second.emailNormalized || normalizeEmail(first.email || second.email || ""),
    importedTotalBookings,
    totalBookings: Math.max(Number(first.totalBookings || 0) || 0, Number(second.totalBookings || 0) || 0, importedTotalBookings),
    importedLastBookingAtText: first.importedLastBookingAtText || second.importedLastBookingAtText || "",
    importedRewardPointsText: first.importedRewardPointsText || second.importedRewardPointsText || "",
    notes: uniqueNotes.join("\n").slice(0, 4000),
    customerNotes: uniqueNotes.join("\n").slice(0, 4000),
    tags,
  };
}

async function loadExistingCustomersForImport(dbClient, businessId, maxRows = 30000) {
  const customersCol = collection(dbClient, `businesses/${businessId}/customers`);
  const rows = [];
  let lastDoc = null;

  while (rows.length < maxRows) {
    const pageSize = Math.min(450, maxRows - rows.length);
    const qBuilt = lastDoc
      ? query(customersCol, orderBy("__name__"), startAfter(lastDoc), limit(pageSize))
      : query(customersCol, orderBy("__name__"), limit(pageSize));
    const snap = await getDocs(qBuilt);
    if (snap.empty) break;
    snap.docs.forEach((docSnap) => {
      const canonical = canonicalCustomerRecord(docSnap.data() || {}, docSnap.id);
      if (customerR29IsActive(canonical)) rows.push(canonical);
    });
    lastDoc = snap.docs[snap.docs.length - 1] || null;
    if (snap.docs.length < pageSize) break;
  }

  if (rows.length >= maxRows) {
    const err = new Error("customer_import_existing_scan_capped");
    err.code = "customer_import_existing_scan_capped";
    throw err;
  }

  const map = new Map();
  rows.forEach((customer) => {
    importCustomerIdentityKeysR29(customer).forEach((key) => {
      const current = map.get(key);
      if (!current) {
        map.set(key, { customer, conflict: false });
        return;
      }
      if (current.conflict || !importCustomerCandidateCompatibleR29(current.customer, customer)) {
        map.set(key, { customer: null, conflict: true });
        return;
      }
      map.set(key, {
        customer: importCustomerBestCandidateR29(current.customer, customer),
        conflict: false,
      });
    });
  });
  return map;
}

function chunkArray(arr, size = 400) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
  return chunks;
}

export async function importCustomersForBusiness(db, businessId, customers = [], options = {}) {
  if (!db || !businessId) throw new Error("missing_db_or_businessId");

  const onProgress = typeof options.onProgress === "function" ? options.onProgress : () => {};
  const rawCleaned = customers
    .map((c) => ({ ...customerPayloadFromBooking({ ...c, source: "imported" }), ...(c.importedProfile ? { importedProfile: c.importedProfile } : {}) }))
    .filter((c) => c.name || c.phoneNormalized || c.emailNormalized);

  const inFileMap = new Map();
  let duplicateRowsInFile = 0;
  rawCleaned.forEach((customer) => {
    const key = (options.strictContactMatch ? (customer.phoneNormalized || customer.emailNormalized) : importCustomerIdentityKey(customer)) || `row:${inFileMap.size}`;
    if (inFileMap.has(key)) {
      duplicateRowsInFile += 1;
      inFileMap.set(key, mergeImportCustomerRecords(inFileMap.get(key), customer));
    } else {
      inFileMap.set(key, customer);
    }
  });

  const cleaned = Array.from(inFileMap.values()).map((customer) => {
    const { phoneNormalized, emailNormalized, nameKey } = buildCustomerSearchKeys({
      name: customer.name || customer.customerName || "Cliente",
      phone: customer.phoneNormalized || customer.phone || "",
      email: customer.emailNormalized || customer.email || "",
    });
    const phoneDigits = onlyDigits(phoneNormalized || customer.phone || "");
    return {
      ...customer,
      phone: phoneNormalized || customer.phone || "",
      phoneNormalized,
      phoneE164: phoneNormalized,
      email: emailNormalized || customer.email || "",
      emailNormalized,
      nameKey,
      searchableName: nameKey,
      searchText: buildCustomerSearchTextForImport({ ...customer, phoneNormalized, emailNormalized, nameKey }),
      phoneDigits,
      whatsappDigits: phoneDigits,
    };
  });

  const existingByIdentity = await loadExistingCustomersForImport(
    db,
    businessId,
    Number(options.maxExistingScan || 30000)
  ).catch((err) => {
    console.error("[customerCrm] import stopped because existing customers could not be verified", err);
    const wrapped = new Error("No pudimos verificar todos los clientes existentes. La importación se detuvo para evitar crear duplicados.");
    wrapped.code = err?.code || "customer_import_existing_scan_failed";
    throw wrapped;
  });

  const batchId = options.dryRun ? null : (await crmWorkspace(businessId, 'beginImport', {source:options.source || customers[0]?.importedProfile?.source || 'Archivo'})).batchId;
  const chunks = chunkArray(cleaned, 40);
  let chunkIndex = 0;
  let imported = 0;
  let processed = 0;
  let created = 0;
  let updated = 0;
  let skippedConflicts = 0;

  for (const group of chunks) {
    const batch = writeBatch(db);
    let writesInBatch = 0;
    const recoveryRows = [];

    group.forEach((customer) => {
      const candidateKeys = importCustomerIdentityKeysR29(customer).filter(key => !options.strictContactMatch || !key.startsWith("name:"));
      const candidates = candidateKeys
        .map((key) => ({ key, entry: existingByIdentity.get(key) }))
        .filter((row) => row.entry);

      const inputHasStrongIdentity = Boolean(
        customerR29Phone(customer) || customerR29Email(customer) || customerR29Uid(customer)
      );
      const strongCandidates = candidates.filter((row) => (
        row.key.startsWith("phone:") || row.key.startsWith("email:")
      ));
      const nameCandidates = candidates.filter((row) => row.key.startsWith("name:"));
      const selectedCandidateRows = strongCandidates.length
        ? strongCandidates
        : nameCandidates.filter((row) => {
            const candidate = row.entry?.customer;
            if (!inputHasStrongIdentity) return true;
            // A new phone/email may safely upgrade an empty same-name shell,
            // but must not overwrite a different contact identity.
            return candidate && !customerR29Phone(candidate) && !customerR29Email(candidate) && !customerR29Uid(candidate);
          });

      const hasConflict = selectedCandidateRows.some((row) => row.entry?.conflict === true);
      const compatibleCustomers = selectedCandidateRows
        .map((row) => row.entry?.customer)
        .filter(Boolean)
        .filter((row, index, arr) => arr.findIndex((x) => String(x.id) === String(row.id)) === index);

      const incompatibleMatches = compatibleCustomers.some((a, index) =>
        compatibleCustomers.slice(index + 1).some((b) => !importCustomerCandidateCompatibleR29(a, b))
      );

      if (hasConflict || incompatibleMatches || (options.strictContactMatch && compatibleCustomers.some(row => !importCustomerCandidateCompatibleR29(row, customer)))) {
        skippedConflicts += 1;
        return;
      }

      const existing = compatibleCustomers.sort(customerR29StableCompare)[0] || null;
      const newRef = existing?.id
        ? doc(db, `businesses/${businessId}/customers`, existing.id)
        : doc(collection(db, `businesses/${businessId}/customers`));
      const customerId = existing?.id || newRef.id;
      const ref = newRef;
      const merged = existing ? mergeImportCustomerRecords(existing, customer) : customer;
      const nowPatch = {
        ...merged,
        __documentId: customerId,
        id: customerId,
        customerId,
        clientId: customerId,
        businessId,
        source: existing?.source || "imported",
        importSource: "crm_file_import_v24",
        hasUserAccount: merged.hasUserAccount === true,
        linkedUserId: merged.linkedUserId || null,
        linkedUserUid: merged.linkedUserUid || merged.linkedUserId || null,
        userId: merged.userId || merged.linkedUserId || null,
        userUid: merged.userUid || merged.linkedUserUid || merged.linkedUserId || null,
        totalBookings: Number(existing?.totalBookings || 0),
        importedTotalBookings: Number(merged.importedTotalBookings || merged.totalBookings || 0) || 0,
        lastBookingAt: merged.lastBookingAt || null,
        hiddenFromCrm: existing?.hiddenFromCrm === true,
        archived: existing?.archived === true,
        updatedAt: serverTimestamp(),
        crmImportUpdatedAt: serverTimestamp(),
        ...(existing ? { whatsappOptOut: existing.whatsappOptOut === true, whatsappOptIn: existing.whatsappOptIn === true, customerWhatsappConsent: existing.customerWhatsappConsent === true } : {}),
      };
      if (!existing) {
        nowPatch.createdAt = serverTimestamp();
        nowPatch.crmImportCreatedAt = serverTimestamp();
        created += 1;
      } else {
        updated += 1;
      }
      batch.set(ref, nowPatch, { merge: true });
      recoveryRows.push({id:customerId,existed:!!existing,expectedUpdatedAt:existing?.updatedAt?.toMillis ? existing.updatedAt.toMillis() : existing?.updatedAt?.seconds !== undefined ? existing.updatedAt.seconds*1000+Math.floor((existing.updatedAt.nanoseconds||0)/1e6) : Date.parse(existing?.updatedAt||'')||0,patch:Object.fromEntries(Object.entries(nowPatch).filter(([key])=>!['updatedAt','crmImportUpdatedAt','createdAt','crmImportCreatedAt'].includes(key)))});
      writesInBatch += 1;
      importCustomerIdentityKeysR29({ ...existing, ...nowPatch, id: customerId }).forEach((key) => {
        existingByIdentity.set(key, {
          customer: { ...existing, ...nowPatch, id: customerId },
          conflict: false,
        });
      });
    });

    if (writesInBatch > 0 && !options.dryRun) await crmWorkspace(businessId, 'importChunk', {batchId,chunkId:'chunk-'+chunkIndex,rows:recoveryRows});
    chunkIndex += 1;
    imported += writesInBatch;
    processed += group.length;
    onProgress({
      imported,
      processed,
      skippedConflicts,
      total: cleaned.length,
      percent: Math.round((processed / Math.max(1, cleaned.length)) * 100),
    });
  }

  if (batchId) await crmWorkspace(businessId, 'finishImport', {batchId,skippedConflicts});
  return {
    batchId,
    imported,
    total: cleaned.length,
    rawTotal: rawCleaned.length,
    created,
    updated,
    skippedConflicts,
    duplicateRowsInFile,
    deduped: duplicateRowsInFile,
  };
}

export async function listCustomersForBusiness(db, businessId, { pageSize = 40, afterDoc = null } = {}) {
  if (!db || !businessId) throw new Error("missing_db_or_businessId");
  const customersCol = collection(db, `businesses/${businessId}/customers`);
  const q = query(customersCol, orderBy("nameKey"), limit(pageSize));
  const snap = await getDocs(q);
  return { customers: snap.docs.map((d) => ({ id: d.id, ...d.data() })), lastDoc: snap.docs[snap.docs.length - 1] || null };
}

function normalizeHeaderName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[º°]/g, "")
    .replace(/\s+/g, " ");
}

function mapCustomerRow(row = {}) {
  const normalized = {};
  Object.keys(row || {}).forEach((key) => {
    normalized[normalizeHeaderName(key)] = row[key];
  });

  const get = (...keys) => {
    for (const key of keys) {
      const v = normalized[normalizeHeaderName(key)];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
    return "";
  };

  const tagText = get("tags", "etiquetas", "categoria", "categoría", "tipo");
  const tags = tagText ? tagText.split(/[;|,]/).map((x) => x.trim()).filter(Boolean) : [];

  const importedTotalBookings = Number(get(
    "numero de reservas",
    "número de reservas",
    "reservas",
    "total reservas",
    "bookings",
    "total bookings"
  ) || 0);

  const importedLastBookingAtText = get(
    "ultima fecha de reserva",
    "última fecha de reserva",
    "last booking date",
    "ultima visita",
    "última visita"
  );

  const importedRewardPointsText = get(
    "puntos de recompensa disponibles",
    "reward points",
    "rewards",
    "puntos"
  );

  const notesFromFile = get("notes", "notas", "nota", "observaciones", "comentarios", "comments");

  return {
    name: get("name", "nombre", "cliente", "customer", "client", "full name", "nombre completo"),
    phone: get(
      "phone",
      "telefono",
      "teléfono",
      "numero de telefono",
      "número de teléfono",
      "celular",
      "whatsapp",
      "mobile",
      "phone number"
    ),
    email: get("email", "correo", "mail", "correo electronico", "correo electrónico"),
    notes: notesFromFile,
    tags,
    importedTotalBookings,
    importedLastBookingAtText,
    importedRewardPointsText,
  };
}

export function parseCustomersCsv(csvText) {
  const text = String(csvText || "").trim();
  if (!text) return [];

  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];

  const splitLine = (line) => {
    const out = [];
    let cur = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];

      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if ((ch === "," || ch === ";") && !inQuotes) {
        out.push(cur.trim());
        cur = "";
      } else {
        cur += ch;
      }
    }

    out.push(cur.trim());
    return out;
  };

  const headers = splitLine(lines[0]);
  const rows = lines.slice(1).map(splitLine);

  return rows
    .map((row) => {
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = row[idx] || "";
      });
      return mapCustomerRow(obj);
    })
    .filter((c) => c.name || c.phone || c.email);
}

export async function parseCustomersFile(file) {
  if (!file) return [];

  const fileName = String(file.name || "").toLowerCase();

  if (fileName.endsWith(".csv")) {
    const text = await file.text();
    return parseCustomersCsv(text);
  }

  if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames && workbook.SheetNames[0];

    if (!sheetName) return [];

    const worksheet = workbook.Sheets[sheetName];
    const jsonRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

    return jsonRows.map(mapCustomerRow).filter((c) => c.name || c.phone || c.email);
  }

  throw new Error("Formato no soportado. Usa .csv, .xlsx o .xls");
}
